import type { ApiErrorBody } from '@provenance/shared';

/**
 * A minimal fetch wrapper for the public pages.
 *
 * Deliberately not the console's client: there is no session, no bearer token and no
 * refresh, so importing that machinery here would ship an authentication system to a
 * customs officer checking one certificate number.
 */
export class PublicApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Array<{ path: string; message: string }>,
  ) {
    super(message);
    this.name = 'PublicApiError';
  }

  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.details ?? []).map((d) => [d.path, d.message]));
  }
}

export async function publicRequest<T>(
  path: string,
  init?: { method?: 'GET' | 'POST'; body?: unknown; headers?: Record<string, string> },
): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? 'GET',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
    body: init?.body ? JSON.stringify(init.body) : undefined,
  });

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    throw new PublicApiError(res.status, 'internal_error', 'The service is not responding properly.');
  }

  const body = await res.json();
  if (!res.ok) {
    const error = (body as ApiErrorBody).error;
    throw new PublicApiError(
      res.status,
      error?.code ?? 'internal_error',
      error?.message ?? 'Something went wrong.',
      error?.details,
    );
  }
  return body as T;
}
